import React, { useEffect, useState } from 'react'

import {
    Button,
    CircularProgress,
    IconButton,
    Popover,
    Theme,
    Tooltip,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import CloseIcon from '@material-ui/icons/Close'

import NumberFormat from 'react-number-format'

import { CalendarPopoverState, FormState } from '../types'
import { Container, StyledProgressBar } from '../../../components'
import {
    CreateScheduleRequest,
    ScheduleDetailActionThunk,
    UpdateScheduleRequest,
} from '../../../store'
import { ScheduleForm } from './ScheduleForm'
import { axiosInstance, isHex, resolveRoute, Routes } from '../../../helpers'
import {
    Inspection,
    InspectionType,
    Schedule,
    Service,
    User,
    WorkSpaceUser,
} from '../../../models'
import { ScheduleReportPopOver } from './ScheduleReportPopover/ScheduleReportPopover'
import { DateTime } from 'luxon'
import { useTimezone } from '../../../contexts/timezone'
import { ReactComponent as ClipboardTickSBIcon } from '../../../assets/icons/clipboard-tick.svg'
import { useHistory } from 'react-router-dom'
import { typographyStyle } from '../../../theme/typographyStyle'
import {
    DraftConfirmWizard,
    scheduleNeedsConfirmWizard,
} from './DraftConfirmWizard'

interface Props {
    formState: FormState
    theme: Theme
    loading?: boolean
    workspaceUser?: WorkSpaceUser
    inspectionList: Inspection[]
    userList: User[]
    inspectionTypeList: InspectionType[]
    serviceList: Service[]
    onClose: () => void
    createSchedule: (req: CreateScheduleRequest) => ScheduleDetailActionThunk
    updateSchedule: (req: UpdateScheduleRequest) => ScheduleDetailActionThunk
    navigateToScheduleDetail: (scheduleId: number) => void
    syncBedStatuses: (schedule: Schedule) => Promise<void>
    getUnitReport: (schedule: Schedule) => Promise<void>
    onReloadData?: () => void
}
export const ScheduleDetailPopover = (props: Props) => {
    const {
        formState,
        onClose,
        theme,
        navigateToScheduleDetail,
        workspaceUser,
        serviceList,
        updateSchedule,
        onReloadData,
    } = props

    const history = useHistory()
    const popoverState = formState.popovers

    const schedule = popoverState.projectPopover.schedule

    const [color, setColor] = useState(
        schedule?.color ?? theme.palette.primary.main,
    )

    const contrastColor = isHex(color)
        ? theme.palette.getContrastText(color)
        : '#000'

    const [displayMode, setDisplayMode] = useState(DisplayMode.DETAIL)

    const showConfirmWizard =
        popoverState.projectPopover.open &&
        !!schedule &&
        scheduleNeedsConfirmWizard(schedule)

    useEffect(() => {
        // Determine what view we are going to display
        // 1. Schedule detail
        // 2. Edit / create (new project only — drafts use DraftConfirmWizard)
        if (schedule === null || schedule === undefined) {
            setDisplayMode(DisplayMode.CREATE_EDIT)
        } else if (scheduleNeedsConfirmWizard(schedule)) {
            setColor(schedule.color)
            setDisplayMode(DisplayMode.DETAIL)
        } else {
            setColor(schedule.color)
            setDisplayMode(DisplayMode.DETAIL)
        }
    }, [schedule])

    const [advancedPanelOpen, setAdvancedPanelOpen] = useState(false)
    const popoverW =
        displayMode === DisplayMode.CREATE_EDIT
            ? FORM_W + (advancedPanelOpen ? ADVANCED_PANEL_W : 0)
            : DETAIL_W

    return (
        <>
            <Popover
                anchorReference="anchorPosition"
                anchorPosition={{
                    top: formState.meta.yPos,
                    left: formState.meta.xPos,
                }}
                open={popoverState.projectPopover.open && !showConfirmWizard}
                onClose={() => {
                    setAdvancedPanelOpen(false)
                    onClose()
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Container
                    style={{
                        minHeight: POPOVER_H,
                        maxHeight: POPOVER_H,
                        minWidth: popoverW,
                        maxWidth: popoverW,
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    {/* Header */}
                    <Container
                        style={{
                            padding: theme.spacing(1),
                            borderBottom: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: color,
                            minHeight:
                                displayMode === DisplayMode.CREATE_EDIT
                                    ? HEADER_H_CREATE_EDIT
                                    : HEADER_H,
                            maxHeight:
                                displayMode === DisplayMode.CREATE_EDIT
                                    ? HEADER_H_CREATE_EDIT
                                    : HEADER_H,
                        }}
                    >
                        {schedule && (
                            <Tooltip title={`Go to ${schedule.name}`}>
                                <IconButton
                                    style={{
                                        marginRight: theme.spacing(1),
                                    }}
                                    onClick={() => {
                                        navigateToScheduleDetail(schedule.id)
                                    }}
                                >
                                    <ExitToAppIcon htmlColor={contrastColor} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Container
                            style={{
                                flexDirection: 'column',
                                flex: 1,
                                color: contrastColor,
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                }}
                            >
                                {schedule?.review_status === 'DRAFT'
                                    ? `${schedule.name} (Draft)`
                                    : schedule?.name ?? 'New Project'}
                            </span>

                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                }}
                            >
                                {schedule?.schedule_id}
                            </span>
                        </Container>

                        <IconButton onClick={onClose}>
                            <CloseIcon htmlColor={contrastColor} />
                        </IconButton>
                    </Container>
                    {/* Body */}

                    {displayMode === DisplayMode.DETAIL && (
                        <ScheduleDetail
                            {...props}
                            schedule={schedule ?? null}
                            color={color}
                            setColor={setColor}
                        />
                    )}

                    {displayMode === DisplayMode.CREATE_EDIT && (
                        <ScheduleForm
                            {...props}
                            schedule={schedule ?? null}
                            color={color}
                            setColor={setColor}
                            initialStartDate={formState.meta.date}
                            apartment={workspaceUser?.active_workspace}
                            onAdvancedSettingsOpenChange={setAdvancedPanelOpen}
                        />
                    )}
                </Container>
            </Popover>
            {schedule && showConfirmWizard && (
                <DraftConfirmWizard
                    open
                    schedule={schedule}
                    serviceList={serviceList}
                    onClose={onClose}
                    updateSchedule={async (req) => {
                        await updateSchedule(req)
                    }}
                    onConfirmed={({ openedScheduler, scheduleId }) => {
                        onClose()
                        onReloadData?.()
                        if (openedScheduler) {
                            const projectActive = schedule.active !== false
                            const route = projectActive
                                ? Routes.apartmentScheduleDetail
                                : Routes.apartmentScheduleArchivedDetail
                            history.push({
                                pathname: resolveRoute(
                                    route,
                                    ':id',
                                    scheduleId,
                                ),
                                state: { openAutoScheduleSetup: true },
                            })
                        }
                    }}
                />
            )}
        </>
    )
}

interface DetailProps {
    theme: Theme
    schedule: Schedule | null
    loading?: boolean
    formState: FormState
    color: string
    workspaceUser?: WorkSpaceUser
    inspectionList: Inspection[]
    userList: User[]
    inspectionTypeList: InspectionType[]
    setColor: (color: string) => void
    onClose: () => void
    createSchedule: (req: CreateScheduleRequest) => ScheduleDetailActionThunk
    updateSchedule: (req: UpdateScheduleRequest) => ScheduleDetailActionThunk
    navigateToScheduleDetail: (scheduleId: number) => void
    syncBedStatuses: (schedule: Schedule) => Promise<void>
    getUnitReport: (schedule: Schedule) => Promise<void>
}
const ScheduleDetail = (props: DetailProps) => {
    const {
        schedule,
        color,
        setColor,
        formState,
        theme,
        workspaceUser,
        navigateToScheduleDetail,
    } = props

    const popoverState = formState.popovers

    const [editFormOpen, setEditFormOpen] = useState(false)
    const [editAdvancedPanelOpen, setEditAdvancedPanelOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState<ScheduleSummary | null>(null)
    const { currentTimezone } = useTimezone()

    const history = useHistory()

    useEffect(() => {
        if (schedule) {
            setLoading(true)

            axiosInstance
                .get(`workorder/schedule/${schedule.id}/summary/`)
                .then((res) => {
                    setSummary(res.data)
                })
                .finally(() => setLoading(false))
        }
    }, [schedule])

    let startDate = DateTime.now()
    let endDate = DateTime.now()

    let completePercent = 0
    let approvedPercent = 0
    let invoicedPercent = 0
    if (schedule) {
        const newStartDate = DateTime.fromISO(schedule.start_date).setZone(
            currentTimezone,
        )
        const newEndDate = DateTime.fromISO(schedule.end_date).setZone(
            currentTimezone,
        )

        if (newStartDate.isValid) {
            startDate = newStartDate
        }
        if (newEndDate.isValid) {
            endDate = newEndDate
        }
        if (schedule.assigned_count > 0) {
            completePercent =
                (schedule.complete_count / schedule.assigned_count) * 100
            approvedPercent =
                (schedule.approved_count / schedule.assigned_count) * 100
            invoicedPercent =
                (schedule.invoiced_count / schedule.assigned_count) * 100
        }
    }

    const barStyle: React.CSSProperties = {
        marginTop: 5,
        marginBottom: 5,
    }

    const labelStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 300,
        width: 100,
    }

    const valueStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 700,
    }

    const navigateToInspectionDetail = (inspectionId: number) => {
        history.push(resolveRoute(Routes.inspectionDetail, ':id', inspectionId))
    }

    return (
        <Container style={{ flexDirection: 'column', flex: 1 }}>
            {loading && (
                <Container
                    style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                    }}
                >
                    <CircularProgress size={100} />
                </Container>
            )}

            {!loading && (
                <Container style={{ flexDirection: 'column' }}>
                    {/* Date range & edit button */}
                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: theme.spacing(1),
                        }}
                    >
                        <Tooltip
                            title={`${startDate.toFormat(
                                'MM/dd/yyyy hh:mm a z',
                            )} - ${endDate.toFormat('MM/dd/yyyy hh:mm a z')}`}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    color: theme.palette.grey[700],
                                    textAlign: 'center',
                                }}
                            >
                                {startDate.toLocaleString(DateTime.DATE_MED)} -{' '}
                                {endDate.toLocaleString(DateTime.DATE_MED)}
                            </span>
                        </Tooltip>
                        <div style={{ flex: 1 }} />

                        {schedule && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ExitToAppIcon />}
                                onClick={() => {
                                    navigateToScheduleDetail(schedule.id)
                                }}
                                style={{
                                    marginRight: theme.spacing(1),
                                    fontWeight: 700,
                                }}
                            >
                                View Project
                            </Button>
                        )}

                        {schedule?.inspection && (
                            <Tooltip title={schedule.inspection_name ?? ''}>
                                <IconButton
                                    onClick={() =>
                                        schedule.inspection &&
                                        navigateToInspectionDetail(
                                            schedule.inspection,
                                        )
                                    }
                                >
                                    <ClipboardTickSBIcon
                                        style={{
                                            width: 24,
                                            height: 24,
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        <ScheduleReportPopOver
                            schedule={schedule ?? undefined}
                            workspaceUser={workspaceUser}
                        />

                        <Tooltip title="Edit">
                            <IconButton onClick={() => setEditFormOpen(true)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Container>

                    {/* Summary details */}

                    <Container
                        style={{
                            padding: theme.spacing(1),
                            flexDirection: 'column',
                        }}
                    >
                        {/* Row 1 Assignment info */}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                color: theme.palette.grey[700],
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Assignment Analytics
                        </span>

                        <Container
                            style={{
                                marginBottom: theme.spacing(3),
                            }}
                        >
                            {/* Column 1 */}
                            <Container
                                style={{ flexDirection: 'column', width: 200 }}
                            >
                                <Container>
                                    <span style={labelStyle}>Assigned</span>
                                    <span style={valueStyle}>
                                        {summary?.total_workorder_count}
                                    </span>
                                </Container>
                                <Container>
                                    <span style={labelStyle}>Behind</span>
                                    <span style={valueStyle}>
                                        {summary?.behind}
                                    </span>
                                </Container>
                                <Container>
                                    <span style={labelStyle}>Not Started</span>
                                    <span style={valueStyle}>
                                        {summary?.not_started}
                                    </span>
                                </Container>
                                <Container>
                                    <span style={labelStyle}>In Progress</span>
                                    <span style={valueStyle}>
                                        {summary?.in_progress}
                                    </span>
                                </Container>
                            </Container>

                            {/* Column 2 */}
                            <Container style={{ flexDirection: 'column' }}>
                                <Container>
                                    <span style={labelStyle}>Complete</span>
                                    <span style={valueStyle}>
                                        {summary?.complete}
                                    </span>
                                </Container>
                                <Container>
                                    <span style={labelStyle}>Approved</span>
                                    <span style={valueStyle}>
                                        {summary?.approved}
                                    </span>
                                </Container>
                                <Container>
                                    <span style={labelStyle}>Invoiced</span>
                                    <span style={valueStyle}>
                                        {summary?.invoiced}
                                    </span>
                                </Container>
                                <Container>
                                    <span style={labelStyle}>
                                        Invoice Request
                                    </span>
                                    <span style={valueStyle}>
                                        {summary?.invoice_request}
                                    </span>
                                </Container>
                            </Container>
                        </Container>

                        {/* Row 2 Service Info */}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                color: theme.palette.grey[700],
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Services
                        </span>
                        <Container
                            style={{
                                marginBottom: theme.spacing(3),
                                overflowX: 'scroll',
                            }}
                        >
                            {summary?.service_list.map((serviceDetail: any) => {
                                return (
                                    <Container
                                        key={`SERVICE-${serviceDetail.id}`}
                                        style={{
                                            marginRight: theme.spacing(2),
                                            minWidth: 200,
                                            maxWidth: 200,
                                        }}
                                    >
                                        <span>
                                            {serviceDetail.name} (
                                            {serviceDetail.count})
                                        </span>
                                    </Container>
                                )
                            })}
                        </Container>

                        {/* Row 3 Vendor Info */}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                color: theme.palette.grey[700],
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Vendors
                        </span>
                        <Container
                            style={{
                                marginBottom: theme.spacing(3),
                                overflowX: 'scroll',
                            }}
                        >
                            {summary?.vendor_list.map((vendorDetail: any) => {
                                return (
                                    <Container
                                        key={`VENDOR-${vendorDetail.id}`}
                                        style={{
                                            marginRight: theme.spacing(2),
                                            minWidth: 200,
                                            maxWidth: 200,
                                        }}
                                    >
                                        <span>
                                            {vendorDetail.name} (
                                            {vendorDetail.count})
                                        </span>
                                    </Container>
                                )
                            })}
                        </Container>

                        {/* Row 4 Budget Info */}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                color: theme.palette.grey[700],
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Budget Details
                        </span>

                        <Container
                            style={{
                                flexDirection: 'column',
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <Container>
                                <span style={labelStyle}>Set</span>
                                <NumberFormat
                                    style={valueStyle}
                                    prefix="$"
                                    value={schedule?.baseline_budget}
                                    displayType="text"
                                    fixedDecimalScale
                                    thousandSeparator
                                    decimalScale={2}
                                />
                            </Container>
                            <Container>
                                <span style={labelStyle}>Forecast</span>
                                <NumberFormat
                                    style={valueStyle}
                                    prefix="$"
                                    value={summary?.budget_forecast}
                                    displayType="text"
                                    fixedDecimalScale
                                    thousandSeparator
                                    decimalScale={2}
                                />
                            </Container>
                            <Container>
                                <span style={labelStyle}>Invoiced</span>
                                <NumberFormat
                                    style={valueStyle}
                                    prefix="$"
                                    value={summary?.budget_invoiced}
                                    displayType="text"
                                    fixedDecimalScale
                                    thousandSeparator
                                    decimalScale={2}
                                />
                            </Container>
                            <Container>
                                <span style={labelStyle}>Invoiced Out</span>
                                <NumberFormat
                                    style={valueStyle}
                                    prefix="$"
                                    value={summary?.budget_invoiced_out}
                                    displayType="text"
                                    fixedDecimalScale
                                    thousandSeparator
                                    decimalScale={2}
                                />
                            </Container>
                            <Container>
                                <span style={labelStyle}>Total Invoiced</span>
                                <NumberFormat
                                    style={valueStyle}
                                    prefix="$"
                                    value={
                                        (summary?.budget_invoiced ?? 0) +
                                        (summary?.budget_invoiced_out ?? 0)
                                    }
                                    displayType="text"
                                    fixedDecimalScale
                                    thousandSeparator
                                    decimalScale={2}
                                />
                            </Container>
                        </Container>
                    </Container>

                    {/* Progress Bars */}
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            padding: theme.spacing(1),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                color: theme.palette.grey[700],
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Progress
                        </span>
                        <Container>
                            <span style={{ width: 100 }}>Complete</span>
                            <StyledProgressBar
                                customStyle={barStyle}
                                value={completePercent}
                                variant="determinate"
                                barColor={theme.palette.info.main}
                            />
                        </Container>

                        <Container>
                            <span style={{ width: 100 }}>Approved</span>

                            <StyledProgressBar
                                customStyle={barStyle}
                                value={approvedPercent}
                                variant="determinate"
                                barColor={theme.palette.secondary.main}
                            />
                        </Container>

                        <Container>
                            <span style={{ width: 100 }}>Invoiced</span>

                            <StyledProgressBar
                                customStyle={barStyle}
                                value={invoicedPercent}
                                variant="determinate"
                                barColor={theme.palette.primary.main}
                            />
                        </Container>
                    </Container>
                </Container>
            )}

            <Popover
                anchorReference="anchorPosition"
                open={editFormOpen}
                onClose={() => {
                    setEditAdvancedPanelOpen(false)
                    setEditFormOpen(false)
                }}
                anchorPosition={{
                    top: formState.meta.yPos + HEADER_H,
                    left: formState.meta.xPos + DETAIL_W,
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Container
                    style={{
                        minHeight: POPOVER_H - HEADER_H,
                        maxHeight: POPOVER_H - HEADER_H,
                        minWidth:
                            FORM_W +
                            (editAdvancedPanelOpen ? ADVANCED_PANEL_W : 0),
                        maxWidth:
                            FORM_W +
                            (editAdvancedPanelOpen ? ADVANCED_PANEL_W : 0),
                    }}
                >
                    <ScheduleForm
                        {...props}
                        schedule={schedule ?? null}
                        initialStartDate={formState.meta.date}
                        color={color}
                        setColor={setColor}
                        apartment={workspaceUser?.active_workspace}
                        onAdvancedSettingsOpenChange={setEditAdvancedPanelOpen}
                    />
                </Container>
            </Popover>
        </Container>
    )
}

const POPOVER_H = 750

const DETAIL_W = 600
const FORM_W = 400
const ADVANCED_PANEL_W = 340

const HEADER_H = 75
const HEADER_H_CREATE_EDIT = 48

enum DisplayMode {
    DETAIL,
    CREATE_EDIT,
}

type ServiceSummary = {
    id: number
    name: string
    count: number
    color: string
}

type VendorSummary = {
    id: number
    name: string
    count: number
}

type ScheduleSummary = {
    total_workorder_count: number
    behind: number
    not_started: number
    in_progress: number
    complete: number
    approved: number
    invoiced: number
    budget_forecast: number
    budget_invoiced: number
    budget_invoiced_out: number
    service_list: ServiceSummary[]
    vendor_list: VendorSummary[]
    invoice_request: number
}
